import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { BulkSignoffInvite } from "@/components/BulkSignoffInvite";
import { ArrowLeft, BellRing, Check, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CELL = {
  signed: ["bg-emerald-100 text-emerald-800", Check, "Signed"],
  pending: ["bg-amber-50 text-amber-800", Clock, "Requested"],
  overdue: ["bg-red-100 text-red-800", AlertTriangle, "Overdue (7+ days)"],
};

export default function SignoffMatrix() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/me/policies/signoff-matrix").then((r) => setData(r.data)).catch((e) => logError("signoff-matrix", e));
  useEffect(() => { load(); }, []);

  const chase = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/me/policies/signoff-matrix/chase"); toast.success(`Reminders sent: ${r.sent}${r.failed ? ` · ${r.failed} failed` : ""}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send reminders"); } finally { setBusy(false); }
  };

  if (!data) return <div className="max-w-6xl mx-auto px-4 py-14 text-sm text-muted-foreground">Loading sign-off matrix…</div>;
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="signoff-matrix">
      <Link to="/policies" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="signoff-matrix-back"><ArrowLeft className="w-4 h-4" /> My Policies</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="overline text-accent">Staff sign-off</span>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">Who has signed what</h1>
          <p className="text-sm text-muted-foreground mt-2">Every staff member who has signed or been asked to sign a policy. Requests older than 7 days without a signature are overdue.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm">
          <span className="rounded-lg border border-border px-3 py-2"><b data-testid="signoff-matrix-signed">{data.signed}</b> signed</span>
          <span className="rounded-lg border border-border px-3 py-2"><b data-testid="signoff-matrix-pending">{data.pending}</b> awaiting</span>
          <span className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800"><b data-testid="signoff-matrix-overdue">{data.overdue}</b> overdue</span>
          <Button size="sm" disabled={busy || !data.overdue} onClick={chase} data-testid="signoff-matrix-chase">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellRing className="w-4 h-4 mr-1" /> Chase overdue</>}</Button>
        </div>
      </div>
      <BulkSignoffInvite onDone={load} />
      {!data.people.length ? (
        <p className="text-sm text-muted-foreground rounded-2xl border border-border p-8 text-center" data-testid="signoff-matrix-empty">No sign-offs yet. Open any policy and use "Ask staff to sign by email" to start tracking.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="text-xs border-collapse min-w-full" data-testid="signoff-matrix-table">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-card text-left px-3 py-2 border-b border-border min-w-[180px]">Staff member</th>
                {data.policies.map((p) => (
                  <th key={`${p.sector}-${p.title}`} className="px-1 py-2 border-b border-border align-bottom" title={p.title}>
                    <div className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap max-h-40 overflow-hidden text-ellipsis font-medium text-muted-foreground">{p.title}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.people.map((person) => (
                <tr key={person.key} data-testid={`signoff-row-${person.key}`}>
                  <td className="sticky left-0 z-10 bg-card px-3 py-2 border-b border-border">
                    <p className="font-medium">{person.name || person.email}</p>
                    {person.name && person.email && <p className="text-muted-foreground">{person.email}</p>}
                    {person.role && <p className="text-muted-foreground">{person.role}</p>}
                  </td>
                  {data.policies.map((p) => {
                    const c = data.cells[`${person.key}|${p.sector}|${p.title}`];
                    if (!c) return <td key={p.title} className="border-b border-border text-center text-muted-foreground/40">·</td>;
                    const [cls, Icon, label] = CELL[c.status];
                    return (
                      <td key={p.title} className="border-b border-border p-0.5">
                        <div className={`${cls} rounded-md w-7 h-7 flex items-center justify-center mx-auto`} title={`${label} · ${new Date(c.date).toLocaleDateString("en-GB")}`} data-testid={`signoff-cell-${c.status}`}><Icon className="w-3.5 h-3.5" /></div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
        {Object.entries(CELL).map(([k, [cls, Icon, label]]) => <span key={k} className="inline-flex items-center gap-1"><span className={`${cls} rounded w-4 h-4 inline-flex items-center justify-center`}><Icon className="w-2.5 h-2.5" /></span>{label}</span>)}
      </div>
    </div>
  );
}
